import axios, { type AxiosInstance, type AxiosResponse } from 'axios'
import type {
  ApiResponse,
  PagedResult,
  AuthResponseData,
  LoginRequest,
  RegisterRequest,
  SocialAuthRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ForgotPasswordResponse,
  LocationDto,
  SessionDto,
  SpeakerDto,
  SpeakerRequestDto,
  CreateSpeakerRequestRequest,
  MosqueDto,
  LocationSearchParams,
  SessionSearchParams,
  UserProfile,
  UpdateProfileRequest,
  ChangePasswordRequest,
  CreateSessionRequest,
  CreateLocationRequest,
  SessionShareDto,
  CreateShareRequest,
  ShareStatsDto,
  UserDto,
  AlertDto,
  CategoryDto,
  PrayerTimesDto,
  PrayerTimesRangeDto,
  PrayerPreferencesDto,
  UpdatePrayerPreferencesRequest,
  NotificationPreferencesDto,
  UpdateNotificationPreferencesRequest,
} from '@/types'
import {
  getCsrfToken,
  getRefreshToken,
  getToken,
  getTokenExpiresAt,
  hasRefreshCredentials,
  isTokenExpired,
  removeCsrfToken,
  removeRefreshToken,
  removeStoredUser,
  removeToken,
  removeTokenExpiresAt,
  setCsrfToken,
  setRefreshToken,
  setStoredUser,
  setToken,
  setTokenExpiresAt,
} from '@/utils/auth'

const TOKEN_KEY = 'dhikr_token'

const PUBLIC_AUTH_PATHS = [
  '/api/Auth/login',
  '/api/Auth/register',
  '/api/Auth/google',
  '/api/Auth/facebook',
  '/api/Auth/forgot-password',
  '/api/Auth/reset-password',
]

function isPublicAuthRequest(url?: string): boolean {
  if (!url) return false
  return PUBLIC_AUTH_PATHS.some((path) => url.includes(path))
}

class WebAppApiService {
  private client: AxiosInstance
  private refreshPromise: Promise<string | null> | null = null

  constructor() {
    const envConfig = (window as any).ENV_CONFIG || {}
    this.client = axios.create({
      baseURL: envConfig.VITE_API_BASE_URL || import.meta.env.VITE_API_BASE_URL || 'https://api.dhikrsession.com',
      timeout: Number(envConfig.VITE_API_TIMEOUT || import.meta.env.VITE_API_TIMEOUT) || 30000,
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    })

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem(TOKEN_KEY)
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    })

    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = (error.config || {}) as any
        const status = error.response?.status
        const isRefreshCall = originalRequest?.url?.includes('/api/Auth/refresh')
        const canRefresh = hasRefreshCredentials()

        if (status === 401 && !isRefreshCall && canRefresh && !originalRequest?._retry) {
          originalRequest._retry = true
          const newToken = await this.refreshAccessToken()
          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`
            return this.client(originalRequest)
          }
        }

        if (status === 401) {
          const skipRedirect = originalRequest?.skipAuthRedirect === true
            || isPublicAuthRequest(originalRequest?.url)
          if (!skipRedirect) {
            this.clearAuthAndRedirectToLogin()
          }
        }
        return Promise.reject(error)
      },
    )

    window.setInterval(() => {
      this.refreshIfTokenExpiringSoon()
    }, 60000)
  }

  private clearAuthAndRedirectToLogin() {
    this.clearStoredAuth()
    void this.syncAuthStoreLogout()
    const path = window.location.pathname + window.location.search
    const redirect = path && path !== '/' && !path.startsWith('/login') ? '?redirect=' + encodeURIComponent(path) : ''
    window.location.href = '/login' + redirect
  }

  private clearStoredAuth() {
    removeToken()
    removeRefreshToken()
    removeCsrfToken()
    removeTokenExpiresAt()
    removeStoredUser()
  }

  private async syncAuthStoreLogout() {
    try {
      const { useAuthStore } = await import('@/stores/auth')
      useAuthStore().logout()
    } catch {
      // pinia not ready
    }
  }

  private applyAuthPayload(payload: AuthResponseData) {
    setToken(payload.token)
    if (payload.expiresAt) {
      setTokenExpiresAt(payload.expiresAt)
    }
    if (payload.csrfToken) {
      setCsrfToken(payload.csrfToken)
    }
    if (payload.refreshToken) {
      setRefreshToken(payload.refreshToken)
    }
    if (payload.user) {
      setStoredUser({
        id: payload.user.id,
        name: payload.user.name,
        email: payload.user.email,
        preferredLanguage: payload.user.preferredLanguage,
        roles: payload.user.roles || [],
      })
    }
    void this.syncAuthStoreFromPayload(payload)
  }

  private async syncAuthStoreFromPayload(payload: AuthResponseData) {
    try {
      const { useAuthStore } = await import('@/stores/auth')
      const auth = useAuthStore()
      auth.setSessionTokens(
        payload.token,
        payload.expiresAt,
        payload.csrfToken,
        payload.refreshToken,
      )
      if (payload.user) {
        auth.setUser({
          id: payload.user.id,
          name: payload.user.name,
          email: payload.user.email,
          preferredLanguage: payload.user.preferredLanguage,
          roles: payload.user.roles || [],
        })
      }
    } catch {
      // pinia not ready during bootstrap
    }
  }

  /** Restore session on app load when access token expired but refresh credentials exist. */
  async restoreSession(): Promise<boolean> {
    const token = getToken()
    const expiresAt = getTokenExpiresAt()

    if (!token && !hasRefreshCredentials()) {
      return false
    }

    if (token && !isTokenExpired(expiresAt)) {
      await this.syncAuthStoreFromStorage()
      return true
    }

    if (!hasRefreshCredentials()) {
      this.clearStoredAuth()
      await this.syncAuthStoreLogout()
      return false
    }

    const newToken = await this.refreshAccessToken()
    if (newToken) {
      return true
    }

    this.clearStoredAuth()
    await this.syncAuthStoreLogout()
    return false
  }

  private async syncAuthStoreFromStorage() {
    try {
      const { useAuthStore } = await import('@/stores/auth')
      useAuthStore().initializeAuth()
    } catch {
      // pinia not ready
    }
  }

  private async refreshAccessToken(): Promise<string | null> {
    if (!this.refreshPromise) {
      this.refreshPromise = (async () => {
        try {
          const csrfToken = getCsrfToken()
          const storedRefreshToken = getRefreshToken()
          const body = storedRefreshToken ? { refreshToken: storedRefreshToken } : {}
          let response: AxiosResponse<ApiResponse<AuthResponseData>>

          if (csrfToken) {
            response = await this.client.post<ApiResponse<AuthResponseData>>(
              '/api/Auth/refresh',
              body,
              { headers: { 'X-CSRF-Token': csrfToken } },
            )
          } else if (storedRefreshToken) {
            response = await this.client.post<ApiResponse<AuthResponseData>>(
              '/api/Auth/refresh',
              { refreshToken: storedRefreshToken },
            )
          } else {
            return null
          }

          const payload = response.data?.data
          if (!response.data?.success || !payload?.token) {
            return null
          }

          this.applyAuthPayload(payload)
          return payload.token
        } catch {
          return null
        } finally {
          this.refreshPromise = null
        }
      })()
    }

    return this.refreshPromise
  }

  private async refreshIfTokenExpiringSoon() {
    if (!hasRefreshCredentials()) return
    const expiresAt = getTokenExpiresAt()
    if (!expiresAt && getToken()) return

    if (isTokenExpired(expiresAt)) {
      await this.refreshAccessToken()
    }
  }

  // ─── Auth ───

  login(data: LoginRequest) {
    return this.client.post<ApiResponse<AuthResponseData>>('/api/Auth/login', data)
  }

  register(data: RegisterRequest) {
    return this.client.post<ApiResponse<AuthResponseData>>('/api/Auth/register', data)
  }

  googleLogin(token: string) {
    return this.client.post<ApiResponse<AuthResponseData>>('/api/Auth/google', {
      token,
      provider: 'google',
    } as SocialAuthRequest)
  }

  facebookLogin(token: string) {
    return this.client.post<ApiResponse<AuthResponseData>>('/api/Auth/facebook', {
      token,
      provider: 'facebook',
    } as SocialAuthRequest)
  }

  me() {
    return this.client.get<ApiResponse<UserDto>>('/api/Auth/me')
  }

  refreshToken() {
    const csrfToken = getCsrfToken()
    const storedRefreshToken = getRefreshToken()
    const body = storedRefreshToken ? { refreshToken: storedRefreshToken } : {}
    return this.client.post<ApiResponse<AuthResponseData>>(
      '/api/Auth/refresh',
      body,
      csrfToken ? { headers: { 'X-CSRF-Token': csrfToken } } : undefined,
    )
  }

  logout() {
    return this.client.post<ApiResponse<null>>('/api/Auth/logout')
  }

  forgotPassword(data: ForgotPasswordRequest) {
    return this.client.post<ApiResponse<ForgotPasswordResponse>>('/api/Auth/forgot-password', data)
  }

  resetPassword(data: ResetPasswordRequest) {
    return this.client.post<ApiResponse<null>>('/api/Auth/reset-password', data)
  }

  // ─── Locations ───

  searchLocations(params: LocationSearchParams) {
    return this.client.get<ApiResponse<PagedResult<LocationDto>>>('/api/Locations/search', { params })
  }

  getLocation(id: string) {
    return this.client.get<ApiResponse<LocationDto>>(`/api/Locations/${id}`)
  }

  addFavoriteLocation(id: string) {
    return this.client.post<ApiResponse<null>>(`/api/Locations/${id}/favorite`, null, {
      skipAuthRedirect: true,
    } as any)
  }

  removeFavoriteLocation(id: string) {
    return this.client.delete<ApiResponse<null>>(`/api/Locations/${id}/favorite`, {
      skipAuthRedirect: true,
    } as any)
  }

  getFavoriteLocations() {
    return this.client.get<ApiResponse<LocationDto[]>>('/api/Locations/favorites')
  }

  // ─── Sessions ───

  searchSessions(params: SessionSearchParams) {
    return this.client.get<ApiResponse<PagedResult<SessionDto>>>('/api/Sessions/search', { params })
  }

  getCategories() {
    return this.client.get<ApiResponse<CategoryDto[]>>('/api/Categories')
  }

  getSession(id: string) {
    return this.client.get<ApiResponse<SessionDto>>(`/api/Sessions/${id}`)
  }

  createSession(data: CreateSessionRequest) {
    return this.client.post<ApiResponse<string>>('/api/Sessions', data)
  }

  joinSession(id: string) {
    return this.client.post<ApiResponse<null>>(`/api/Sessions/${id}/join`)
  }

  leaveSession(id: string) {
    return this.client.delete<ApiResponse<null>>(`/api/Sessions/${id}/attend`)
  }

  getMyUpcomingSessions() {
    return this.client.get<ApiResponse<SessionDto[]>>('/api/Sessions/upcoming')
  }

  getMyPastSessions() {
    return this.client.get<ApiResponse<SessionDto[]>>('/api/Sessions/past')
  }

  // ─── Mosques ───

  getNearbyMosques(
    lat: number,
    lng: number,
    radiusKm = 10,
    page = 1,
    pageSize = 20,
    locationType?: string,
  ) {
    return this.client.get<ApiResponse<PagedResult<MosqueDto>>>('/api/Mosques/nearby', {
      params: {
        lat,
        lng,
        radiusKm,
        page,
        pageSize,
        ...(locationType ? { locationType } : {}),
      },
    })
  }

  getMosque(id: string) {
    return this.client.get<ApiResponse<MosqueDto>>(`/api/Mosques/${id}`)
  }

  // ─── Speakers ───

  searchSpeakers(params: { query?: string; isVerified?: boolean; page?: number; pageSize?: number }) {
    return this.client.get<ApiResponse<PagedResult<SpeakerDto>>>('/api/Speakers/search', { params })
  }

  getSpeaker(id: string) {
    return this.client.get<ApiResponse<SpeakerDto>>(`/api/Speakers/${id}`)
  }

  followSpeaker(id: string) {
    return this.client.post<ApiResponse<null>>(`/api/Speakers/${id}/follow`)
  }

  unfollowSpeaker(id: string) {
    return this.client.post<ApiResponse<null>>(`/api/Speakers/${id}/unfollow`)
  }

  getFollowedSpeakers() {
    return this.client.get<ApiResponse<SpeakerDto[]>>('/api/Speakers/followed')
  }

  // ─── Speaker Requests ───

  createSpeakerRequest(data: CreateSpeakerRequestRequest) {
    return this.client.post<ApiResponse<string>>('/api/Speakers/request', data)
  }

  getMySpeakerRequest() {
    return this.client.get<ApiResponse<SpeakerRequestDto | null>>('/api/Speakers/my-request')
  }

  // ─── Profile ───

  getProfile() {
    return this.client.get<ApiResponse<UserProfile>>('/api/UserProfiles/profile')
  }

  updateProfile(data: UpdateProfileRequest) {
    return this.client.put<ApiResponse<UserProfile>>('/api/UserProfiles/profile', data)
  }

  changePassword(data: ChangePasswordRequest) {
    return this.client.post<ApiResponse<null>>('/api/UserProfiles/change-password', data)
  }

  // ─── Create Location ───

  createLocation(data: CreateLocationRequest) {
    return this.client.post<ApiResponse<string>>('/api/Locations', data)
  }

  // ─── Session Shares ───

  createShare(data: CreateShareRequest) {
    return this.client.post<ApiResponse<SessionShareDto>>('/api/SessionShares', data)
  }

  getShareStats(sessionId: string) {
    return this.client.get<ApiResponse<ShareStatsDto>>(`/api/SessionShares/stats/${sessionId}`)
  }

  trackShareClick(shortCode: string) {
    return this.client.get<ApiResponse<SessionShareDto>>(`/api/SessionShares/click/${shortCode}`)
  }

  // ─── Firebase Config ───

  getFirebaseConfig() {
    return this.client.get<ApiResponse<{
      apiKey: string
      authDomain: string
      projectId: string
      storageBucket: string
      messagingSenderId: string
      appId: string
      measurementId: string
      vapidKey: string
    }>>('/api/v1/public/firebase-config')
  }

  getFirebaseAndroidConfig() {
    return this.client.get<ApiResponse<{
      apiKey: string
      projectId: string
      storageBucket: string
      messagingSenderId: string
      appId: string
      packageName: string
    }>>('/api/v1/public/firebase-config/android')
  }

  getMobileConfig() {
    return this.client.get<ApiResponse<{
      googleClientId: string
      facebookAppId: string
      firebase: {
        apiKey: string
        projectId: string
        storageBucket: string
        messagingSenderId: string
        appId: string
        packageName: string
      }
    }>>('/api/v1/public/mobile-config')
  }

  getAppDownloads() {
    return this.client.get<ApiResponse<{
      webAppUrl: string
      googlePlayUrl: string
      apkVersion: string
      apkDownloadUrl: string
    }>>('/api/v1/public/app-downloads')
  }

  getAppVersion() {
    return this.client.get<ApiResponse<{
      latestVersion: string
      latestVersionCode: number
      apkDownloadUrl: string
      releaseNotes: string | null
      forceUpdate: boolean
      googlePlayUrl: string | null
    }>>('/api/v1/public/app/version')
  }

  // ─── Device Token (Push Notifications) ───

  updateDeviceToken(deviceToken: string) {
    return this.client.post<ApiResponse<null>>('/api/Auth/device-token', JSON.stringify(deviceToken), {
      headers: { 'Content-Type': 'application/json' },
    })
  }

  // ─── Alerts / Reminders ───

  createPresetAlert(sessionId: string, preset: '5m' | '15m' | '30m' | '1h') {
    return this.client.post<ApiResponse<string>>('/api/Alerts/preset', null, {
      params: { sessionId, preset },
    })
  }

  getAlerts(isActive?: boolean) {
    return this.client.get<ApiResponse<AlertDto[]>>('/api/Alerts', {
      params: isActive != null ? { isActive } : undefined,
    })
  }

  deleteAlert(id: string) {
    return this.client.delete<ApiResponse<null>>(`/api/Alerts/${id}`)
  }

  // ─── Prayer Times ───

  getPrayerTimesRange(lat: number, lng: number, days = 7, start?: string, method?: number) {
    return this.client.get<ApiResponse<PrayerTimesRangeDto>>('/api/PrayerTimes/range', {
      params: {
        lat,
        lng,
        days,
        ...(start ? { start } : {}),
        ...(method != null ? { method } : {}),
      },
    })
  }

  getPrayerPreferences() {
    return this.client.get<ApiResponse<PrayerPreferencesDto>>('/api/PrayerTimes/preferences')
  }

  updatePrayerPreferences(request: UpdatePrayerPreferencesRequest) {
    return this.client.put<ApiResponse<PrayerPreferencesDto>>('/api/PrayerTimes/preferences', request)
  }

  getNotificationPreferences() {
    return this.client.get<ApiResponse<NotificationPreferencesDto>>('/api/NotificationPreferences')
  }

  updateNotificationPreferences(request: UpdateNotificationPreferencesRequest) {
    return this.client.put<ApiResponse<NotificationPreferencesDto>>('/api/NotificationPreferences', request)
  }
}

export const api = new WebAppApiService()
